import { useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import Tabs from 'core/tabs/Tabs';
import PartnerWebhooksTab from './tabs/PartnerWebhooksTab';
import PartnerCountriesTab from './tabs/PartnerCountriesTab';
import PartnerCredentialsTab from './tabs/PartnerCredentialsTab';
import PartnerWebhookHeadersTab from './tabs/PartnerWebhookHeadersTab';
import PartnerFundingSourcesTab from './tabs/PartnerFundingSourcesTab';
import PartnerDetailsTab from 'components/partners/tabs/PartnerDetailsTab';

import { getPartner } from 'app/features/partners/partnerSaga';
import PartnerCustomersTab from './tabs/PartnerCustomersTab';
import LicensePartnerMapperTab from './tabs/LicensePartnerMapperTab';
import { permissions } from 'app/data/permissions';
import { HasPermission, hasUserPermission } from 'hoc/withRole';

function Partner({ partnerId }) {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getPartner(partnerId));
  }, [partnerId]);

  const allTabs = [
    {
      key: 'partner-details',
      tabName: 'Details',
      tabContent: <PartnerDetailsTab />,
      permission: permissions.VIEW_BUSINESS_PROFILE,
    },
    {
      key: 'partner-credentials',
      tabName: 'Credentials',
      tabContent: <PartnerCredentialsTab partnerId={partnerId} />,
      permission: permissions.VIEW_PARTNER_CREDENTIALS_BY_PARTNERID,
    },
    {
      key: 'partner-customers',
      tabName: 'Customers',
      tabContent: <PartnerCustomersTab partnerId={partnerId} />,
      permission: permissions.VIEW_CUSTOMER_BY_PARTNER_ID,
    },
    {
      key: 'partner-webhooks',
      tabName: 'Webhooks',
      tabContent: <PartnerWebhooksTab partnerId={partnerId} />,
      permission: permissions.VIEW_WEBHOOK_LIST,
    },
    {
      key: 'partner-webhook-headers',
      tabName: 'Webhook Headers',
      tabContent: <PartnerWebhookHeadersTab partnerId={partnerId} />,
      permission: permissions.VIEW_WEBHOOK_HEADER_LIST,
    },
    {
      key: 'partner-funding-sources',
      tabName: 'Funding Sources',
      tabContent: <PartnerFundingSourcesTab partnerId={partnerId} />,
      permission: permissions.VIEW_PARTNER_FUNDING_SOURCE_LIST,
    },
    {
      key: 'partner-countries',
      tabName: 'Countries',
      tabContent: (
        <HasPermission permission={permissions.VIEW_PARTNER_COUNTRY_LIST}>
          <PartnerCountriesTab partnerId={partnerId} />
        </HasPermission>
      ),
      permission: permissions.VIEW_PARTNER_COUNTRY_LIST,
    },
    {
      key: 'licensing-partners',
      tabName: 'Licensing Partners',
      tabContent: <LicensePartnerMapperTab partnerId={partnerId} />,
      permission: permissions.VIEW_PARTNER_LICENSE_BY_PARTNERID,
    },
  ];

  const filteredTabs = allTabs.filter(
    (tab) => !tab.permission || hasUserPermission(tab.permission),
  );

  return <Tabs tabs={filteredTabs} />;
}

export default memo(Partner);

Partner.propTypes = {
  partnerId: PropTypes.string.isRequired,
};
